/**
 * Search prefix filters (SPEC §7.7): `#tag`, `@cece`, `is:done`, `is:open`, `is:task`,
 * `is:label`, `is:archived`, `date:sat`, `date:2026-10-04`, `date:week`,
 * `has:amount`, `under:"Kitchen Reno"` (by title).
 */
import { addDays, parseDayWord } from './dates'
import { ancestorsOf, type ResolvedTree } from './tree'
import { displayTitle } from './links'
import { hasValue } from './fields'
import { isUnderTag } from './tags'
import { fieldNamed } from './details'
import { itemKind, type FieldDef, type ItemSnapshot } from './types'

export interface ParsedQuery {
  text: string
  tags: string[]
  people: string[]
  is: Set<string>
  dateFrom?: string
  dateTo?: string
  has: string[]
  under: string[]
}

export function parseSearchQuery(q: string, today: string): ParsedQuery {
  const out: ParsedQuery = { text: '', tags: [], people: [], is: new Set(), has: [], under: [] }
  const rest: string[] = []
  const tokens = q.match(/(?:[^\s"]+:"[^"]*"|"[^"]*"|\S+)/g) ?? []
  for (const raw of tokens) {
    const tok = raw.trim()
    const lower = tok.toLowerCase()
    if (tok.startsWith('#') && tok.length > 1) out.tags.push(lower.slice(1))
    else if (tok.startsWith('@') && tok.length > 1) out.people.push(lower.slice(1))
    else if (lower.startsWith('is:')) out.is.add(lower.slice(3))
    else if (lower.startsWith('has:')) out.has.push(lower.slice(4))
    else if (lower.startsWith('under:')) out.under.push(tok.slice(6).replace(/^"|"$/g, ''))
    else if (lower.startsWith('date:')) {
      const v = lower.slice(5)
      if (v === 'week') {
        out.dateFrom = today
        out.dateTo = addDays(today, 6)
      } else if (v === 'past' || v === 'overdue') {
        out.dateTo = addDays(today, -1)
      } else if (v === 'future' || v === 'upcoming') {
        out.dateFrom = today
      } else if (v.includes('..')) {
        const [a, b] = v.split('..')
        const fa = a ? parseDayWord(a, today) : null
        const fb = b ? parseDayWord(b, today) : null
        if (fa) out.dateFrom = fa
        if (fb) out.dateTo = fb
      } else {
        const d = parseDayWord(v, today)
        if (d) out.dateFrom = out.dateTo = d
        else rest.push(tok)
      }
    } else rest.push(tok.replace(/^"|"$/g, ''))
  }
  out.text = rest.join(' ').trim()
  return out
}

export function hasFilters(p: ParsedQuery): boolean {
  return !!(p.tags.length || p.people.length || p.is.size || p.dateFrom || p.dateTo || p.has.length || p.under.length)
}

/** Who's who, for `@name` and `@me`, and the details registry, for `has:<detail>`. */
export interface SearchCtx {
  items: ReadonlyMap<string, ItemSnapshot>
  tree: ResolvedTree
  users: ReadonlyMap<string, { name: string; email: string }>
  fields: Map<string, FieldDef>
  meId?: string
}

/**
 * Whether an item passes a search's filters (the words aside), the same in the app and for the
 * assistant: `#tag` (and what's under it), `@name` / `@me`, `is:done|open|task|label|archived`,
 * dates, `has:photo|file|link|body|<detail>` (a detail by key or name, with a value) and
 * `under:<title>` (a card above it whose title is or starts with it). Whether archived things
 * count at all is the caller's choice; `is:archived` keeps only them.
 */
export function matchesSearch(it: ItemSnapshot, q: ParsedQuery, ctx: SearchCtx): boolean {
  const { tree } = ctx
  if (q.is.has('archived') && !tree.inArchive.has(it.id)) return false
  for (const t of q.tags) if (!it.tags.some((x) => isUnderTag(x, t))) return false
  for (const p of q.people) {
    if (p === 'me') {
      if (!ctx.meId || it.assignee !== ctx.meId) return false
      continue
    }
    const hit = [...ctx.users.entries()].find(([, u]) => u.name.toLowerCase().startsWith(p) || u.email.toLowerCase().startsWith(p))
    if (!hit || it.assignee !== hit[0]) return false
  }
  if (q.is.has('done') && it.done !== true) return false
  if (q.is.has('open') && it.done !== false) return false
  if (q.is.has('task') && itemKind(it) !== 'task') return false
  // is:event / is:note were the two kinds of label before labels; they still find labels.
  if ((q.is.has('label') || q.is.has('event') || q.is.has('note')) && itemKind(it) !== 'label') return false
  if (q.dateFrom && (!it.date || it.date < q.dateFrom)) return false
  if (q.dateTo && (!it.date || it.date > q.dateTo)) return false
  for (const h of q.has) {
    if (h === 'photo' || h === 'image') {
      if (!it.attachments.some((a) => a.mime.startsWith('image/'))) return false
    } else if (h === 'attachment' || h === 'file') {
      if (!it.attachments.length) return false
    } else if (h === 'link') {
      if (!it.link) return false
    } else if (h === 'body' || h === 'note') {
      if (!it.bodyText) return false
    } else {
      const named = fieldNamed(h, ctx.fields)
      const key = named && !named.isNew ? named.def.key : h
      if (!hasValue(it.props[key])) return false
    }
  }
  if (q.under.length) {
    const titleOf = (x: string) => ctx.items.get(x)?.title
    const anc = ancestorsOf(tree, it.id).map((a) => displayTitle(ctx.items.get(a)?.title ?? '', titleOf).toLowerCase())
    for (const u of q.under) if (!anc.some((a) => a === u.toLowerCase() || a.startsWith(u.toLowerCase()))) return false
  }
  return true
}
